import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import { WithUrqlState } from 'next-urql';
import { PageContainer } from '~/components/elements/PageContainer/PageContainer';
import {
  MedicalInstitutionTopByKeyDocument,
  MedicalInstitutionTopByIdDocument,
} from 'gen/medinavi-backend-consumer-graphql/graphql';
import { MedicalInstitutionTopByKey } from '~/components/pages/medical_institution/introduction/MedicalInstitutionTopByKey/MedicalInstitutionTopByKey';
import { MedicalInstitutionTopById } from '~/components/pages/medical_institution/introduction/MedicalInstitutionTopById/MedicalInstitutionTopById';
import { ErrorType, HandledError, handleErrorForSSR, NotFoundError } from '~/types/handled-error';
import { ErrorFromSSR } from '~/components/errors/error-from-ssr/errorFromSSR';
import { Result } from '~/types/result';
import {
  initUrqlMedinaviBackendClientForSSR,
  withUrqlMedinaviBackendClientForSSR,
} from '~/utils/initUrqlMedinaviBackendClientForSSR';

type Specifier =
  | {
      type: 'hospitalUserId';
      hospitalUserId: number;
    }
  | {
      type: 'hospitalUserKey';
      hospitalUserKey: string;
    };

type Props = {
  specifier: Specifier;
};

const MedicalInstitutionIntroPage: NextPage<Result<Props, ErrorType>> = ({ value, error }) => {
  if (error != null) {
    return <ErrorFromSSR errorType={error} />;
  }
  const { specifier } = value;
  return (
    <PageContainer hideHeader>
      {specifier.type === 'hospitalUserKey' ? (
        <MedicalInstitutionTopByKey hospitalUserKey={specifier.hospitalUserKey} />
      ) : (
        <MedicalInstitutionTopById hospitalUserId={specifier.hospitalUserId} />
      )}
    </PageContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<WithUrqlState & Result<Props, ErrorType>>> {
  const BLOCKED_HOSPITAL_USER_KEYS = ['d6ced983-5590-4480-ad3a-4ce9dcddd90b'];

  const { client, extractCacheData } = initUrqlMedinaviBackendClientForSSR();

  const hospitalUserKey = ctx.query['hospital_user_key'];
  const rawHospitalUserId = ctx.query['hospital_user_id'];
  if (typeof hospitalUserKey === 'string') {
    // ブロックリスト判定
    if (BLOCKED_HOSPITAL_USER_KEYS.includes(hospitalUserKey)) {
      return handleErrorForSSR(new NotFoundError());
    }
    const specifier: Specifier = {
      type: 'hospitalUserKey',
      hospitalUserKey,
    };
    const res = await client
      .query(MedicalInstitutionTopByKeyDocument, {
        facilityMemberKey: hospitalUserKey,
      })
      .toPromise();
    if (res.error != null) {
      return handleErrorForSSR(
        new HandledError(res.error, {
          shouldSendToSentry: false,
          requireReStart: false,
        }),
      );
    }
    if (res.data?.medicoFacilityByFacilityMemberKey == null) {
      return handleErrorForSSR(new NotFoundError(res.error));
    }

    return {
      props: {
        urqlState: extractCacheData(),
        value: { specifier },
      },
    };
  } else if (typeof rawHospitalUserId === 'string') {
    const hospitalUserId = Number(rawHospitalUserId);
    const specifier: Specifier = {
      type: 'hospitalUserId',
      hospitalUserId,
    };
    const res = await client
      .query(MedicalInstitutionTopByIdDocument, {
        facilityMemberId: hospitalUserId,
      })
      .toPromise();

    if (res.error != null) {
      return handleErrorForSSR(
        new HandledError(res.error, {
          shouldSendToSentry: false,
          requireReStart: false,
        }),
      );
    }
    if (res.data?.medicoFacilityByFacilityMemberId == null) {
      return handleErrorForSSR(new NotFoundError(res.error));
    }

    return {
      props: {
        urqlState: extractCacheData(),
        value: { specifier },
      },
    };
  }

  return {
    notFound: true,
  };
}

export default withUrqlMedinaviBackendClientForSSR(MedicalInstitutionIntroPage);
